import React, { useEffect, useRef, useCallback } from 'react';
import './WordCelebration.css';

interface WordCelebrationProps {
  isActive: boolean;
  intensity: number; // 1-6 based on score (higher = bigger celebration)
  onAnimationComplete?: () => void;
}

interface Firework {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
  burst: number;
}

const WordCelebration: React.FC<WordCelebrationProps> = ({
  isActive,
  intensity,
  onAnimationComplete
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const fireworksRef = useRef<Firework[]>([]);
  const isActiveRef = useRef(isActive);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Update ref when prop changes
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
  }, []);

  const drawFireworks = useCallback((ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    fireworksRef.current.forEach(firework => {
      const { x, y, size, color, life, maxLife } = firework;
      const opacity = Math.max(0, life / maxLife);
      const intensityFactor = intensity / 6; // 0.167 to 1.0 based on score
      
      // Enhanced glow effect based on intensity
      const shadowBlur = 8 + intensity * 4; // 12-32px blur
      const outerGlow = shadowBlur * 1.5;
      
      ctx.globalAlpha = opacity;
      
      // Draw outer glow for light mode visibility
      ctx.shadowBlur = outerGlow;
      ctx.shadowColor = color;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, size * 1.2, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw bright inner core
      ctx.shadowBlur = shadowBlur;
      ctx.shadowColor = color;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      
      // Add bright white center for high intensity
      if (intensity >= 4) {
        ctx.shadowBlur = shadowBlur * 0.5;
        ctx.shadowColor = '#ffffff';
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity * intensityFactor * 0.8})`;
        ctx.beginPath();
        ctx.arc(x, y, size * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  }, [intensity]);

  const animate = useCallback(() => {
    if (!isActiveRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Update fireworks physics with intensity-based effects
    const intensityFactor = intensity / 6;
    
    fireworksRef.current = fireworksRef.current.map(firework => {
      // Higher intensity fireworks have less air resistance (more dramatic)
      const airResistance = 0.995 - intensityFactor * 0.005; // 0.99 to 0.995
      const gravity = 0.15 + intensityFactor * 0.1; // 0.15 to 0.25
      
      return {
        ...firework,
        x: firework.x + firework.dx,
        y: firework.y + firework.dy,
        dx: firework.dx * airResistance,
        dy: firework.dy * airResistance + gravity,
        life: firework.life - (0.8 + intensityFactor * 0.4), // Higher intensity fades slightly faster
        size: Math.max(0.3, firework.size * (firework.life / firework.maxLife * 0.9 + 0.1))
      };
    }).filter(firework => firework.life > 0);

    drawFireworks(ctx, canvas);

    if (fireworksRef.current.length === 0 && onAnimationComplete) {
      onAnimationComplete();
      return;
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [drawFireworks, onAnimationComplete, intensity]);

  const createFireworks = useCallback((
    centerX: number,
    centerY: number,
    spreadWidth: number,
    burstId: number
  ) => {
    if (!isActiveRef.current) return;

    // Brighter, more vibrant colors for better light mode visibility
    const colors = [
      '#ff6b00', // Bright orange
      '#ff1744', // Bright red  
      '#00e676', // Bright green
      '#2196f3', // Bright blue
      '#9c27b0', // Bright purple
      '#ffc107', // Bright amber
      '#ff5722', // Deep orange
      '#3f51b5'  // Indigo
    ];
    
    const intensityFactor = intensity / 6; // 0.167 to 1.0 based on score
    const fireworksPerBurst = Math.max(3, 4 + intensity);
    const baseId = Date.now() + burstId * 1000;

    for (let i = 0; i < fireworksPerBurst; i++) {
      const angle = (Math.PI * 2 * i) / fireworksPerBurst + Math.random() * 0.4;
      
      // Scale speed with intensity - higher scores get more explosive spread
      const baseSpeed = 2 + Math.random() * 2;
      const speed = baseSpeed * (0.8 + intensityFactor * 0.7); // 0.8x to 1.5x speed
      
      // Scale spread with intensity
      const intensitySpread = spreadWidth * (0.6 + intensityFactor * 0.8); // 0.6x to 1.4x spread
      const startX = centerX + (Math.random() - 0.5) * intensitySpread;
      const startY = centerY + (Math.random() - 0.5) * 20;

      // Scale size with intensity - higher scores get bigger particles  
      const baseSize = 2 + Math.random() * 2;
      const size = baseSize * (0.7 + intensityFactor * 0.8); // 0.7x to 1.5x size
      
      // Scale life with intensity - higher scores get longer-lasting fireworks
      const baseLife = 120;
      const life = baseLife + intensity * 15; // 135-210 frames

      fireworksRef.current.push({
        id: baseId + i,
        x: startX,
        y: startY,
        dx: Math.cos(angle) * speed,
        dy: Math.sin(angle) * speed,
        size: size,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: life,
        maxLife: life,
        burst: burstId
      });
    }
  }, [intensity]);

  const startCelebration = useCallback(() => {
    if (!isActiveRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Find word display for positioning
    const wordDisplay = document.querySelector('.word-display');
    if (!wordDisplay) return;

    const rect = wordDisplay.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    
    // Calculate center position relative to canvas
    const centerX = rect.left + rect.width / 2 - canvasRect.left;
    const centerY = rect.top + rect.height / 2 - canvasRect.top;
    const spreadWidth = rect.width * 0.8;

    // Create sequential bursts
    const burstCount = Math.max(1, Math.min(intensity - 1, 3));
    
    for (let burst = 0; burst < burstCount; burst++) {
      const delay = burst * 300; // 300ms between bursts
      
      const timeout = setTimeout(() => {
        createFireworks(centerX, centerY, spreadWidth, burst);
      }, delay);
      
      timeoutsRef.current.push(timeout);
    }

    // Auto-complete timeout
    const completeTimeout = setTimeout(() => {
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }, burstCount * 300 + 2000);
    
    timeoutsRef.current.push(completeTimeout);
  }, [intensity, createFireworks, onAnimationComplete]);

  // Setup canvas and start animation when active
  useEffect(() => {
    if (!isActive) {
      fireworksRef.current = [];
      clearAllTimeouts();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size to match container
    const updateCanvasSize = () => {
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
      }
    };

    updateCanvasSize();
    
    // Start celebration and animation
    const startTimer = setTimeout(() => {
      startCelebration();
      animationRef.current = requestAnimationFrame(animate);
    }, 16); // Start on next frame

    timeoutsRef.current.push(startTimer);

    return () => {
      clearAllTimeouts();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, startCelebration, animate, clearAllTimeouts]);

  if (!isActive) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      className="word-celebration-canvas"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
      aria-hidden="true"
    />
  );
};

export default WordCelebration;